/**
 * Unified auth API: single login (one token), /me for has_company and is_admin.
 */

import axios from 'axios'
import { getCustomerToken } from 'shared/store/authStore'

function getApiBase(): string {
  const env = (import.meta.env.VITE_API_URL ?? '').trim()
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return ''
  }
  return env
}
const API_URL = getApiBase()
const baseURL = API_URL ? `${API_URL}/api/v1` : '/api/v1'

const authApi = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
})

authApi.interceptors.request.use((config) => {
  const token = getCustomerToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

export type UnifiedLoginResponse = {
  token_type: 'company' | 'admin' | 'customer'
  access_token?: string
  requires_2fa?: boolean
  requires_email_verification?: boolean
  temp_token?: string
  user_id?: string
  email?: string
  role?: string
}

export type MeResponse = {
  user_id: string
  email: string
  role: string
  has_company: boolean
  is_admin: boolean
  company_id?: string | null
  company_name?: string | null
  locale: string // preferred UI/email language: bhs, hr, sr, en (backend); map bhs -> bs for i18n
}

export const unifiedAuth = {
  login: (email: string, password: string) =>
    authApi.post<UnifiedLoginResponse>('/auth/login', { email, password }),

  verify2fa: (tempToken: string, code: string) =>
    authApi.post<UnifiedLoginResponse>('/auth/login/verify-2fa', { temp_token: tempToken, code }),

  registerCustomer: (email: string, password: string) =>
    authApi.post<{ message: string; requires_verification?: boolean }>('/auth/register', { email, password }),

  verifyEmail: (email: string, otp_code: string) =>
    authApi.post<{ message: string; verified: boolean }>('/auth/verify-email', { email, otp_code }),

  resendOtp: (email: string) =>
    authApi.post<{ message: string }>('/auth/resend-otp', { email }),

  /** Current user and permissions (has_company, is_admin, locale). Refresh every minute from frontend. */
  me: () => authApi.get<MeResponse>('/auth/me'),

  /** Update current user profile (e.g. preferred_locale). Returns updated /me. */
  patchMe: (body: { preferred_locale?: string }) =>
    authApi.patch<MeResponse>('/auth/me', body),

  /** Create a company for the current user (logged in). No email/password. */
  addCompany: (data: { company_name: string; tin: string; business_unit?: string; address?: string }) =>
    authApi.post<{ message: string; company_id: string }>('/auth/company', data),
}
